import { apiClient } from '../lib/apiClient';

/**
 * Test intervals of an aquarium (Reef Guard #1823): how often each parameter
 * should be tested — the user's choice or the heuristic default (alkalinity
 * from the measured KH usage) — with the last test and the next due date.
 */
export interface TestInterval {
  readonly parameterId: string;
  /** Hours between tests; null = reminders off. */
  readonly intervalHours: number | null;
  readonly mode: 'default' | 'custom' | 'off';
  readonly defaultHours: number;
  readonly basis: 'default' | 'kh_usage_high' | 'kh_usage_moderate' | 'kh_usage_low';
  readonly lastTestedAt: string | null;
  readonly dueAt: string | null;
  readonly overdue: boolean;
  readonly dosingActive: boolean;
}

const base = (aquariumId: string): string => `/api/v1/aquariums/${aquariumId}/test-intervals`;

export function testIntervalsKey(aquariumId: string | null | undefined): readonly unknown[] {
  return ['testIntervals', aquariumId];
}

/** GET …/test-intervals */
export async function listTestIntervals(aquariumId: string): Promise<TestInterval[]> {
  const response = await apiClient.get<{ data: TestInterval[] }>(base(aquariumId));
  return Array.isArray(response?.data) ? response.data : [];
}

/** PUT …/test-intervals/:parameterId — hours (1–720) or null = reminders off. */
export async function saveTestInterval(aquariumId: string, parameterId: string, intervalHours: number | null): Promise<TestInterval[]> {
  const response = await apiClient.put<{ data: TestInterval[] }>(`${base(aquariumId)}/${encodeURIComponent(parameterId)}`, {
    interval_hours: intervalHours,
  });
  return response.data;
}

/** DELETE …/test-intervals/:parameterId — back to the default. */
export async function resetTestInterval(aquariumId: string, parameterId: string): Promise<TestInterval[]> {
  const response = await apiClient.delete<{ data: TestInterval[] }>(`${base(aquariumId)}/${encodeURIComponent(parameterId)}`);
  return response.data;
}
